<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('VIEW_SMS_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(dateCreated)="data">
        {{ data.value ? toDateWithCustomTime(data.value) : '' }}
      </template>
      <template #cell(deliveryStatus)="data">
        <b-badge
          v-if="data.value"
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge></template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="fetchSMSReport"
      @cancel="resetFilters"
    >
      <b-form>
        <b-row class="match-height">
          <b-col md="6">
            <InputField
              id="phone-number"
              v-model="filter.phoneNumber"
              :label="$t('Phone Number')"
              :placeholder="$t('Enter Phone Number')"
              type="text"
            />
          </b-col>
          <!-- Account Number -->
          <b-col md="6">
            <InputField
              id="account-number"
              v-model="filter.accountNumber"
              :label="$t('Account Number')"
              :placeholder="$t('Enter Account Number')"
              type="text"
            />
          </b-col>
          <!-- Account Number -->
          <!-- Reference Number -->
          <b-col md="6">
            <InputField
              id="reference-number"
              v-model="filter.referenceNumber"
              :label="$t('Reference Number')"
              :placeholder="$t('Enter Reference Number')"
              type="text"
              for="reference-number"
            />
          </b-col>
          <!-- Reference Number -->
          <b-col md="6">
            <SelectField
              id="status"
              v-model="filter.status"
              :reduce="option => option"
              :label="$t('Status')"
              :placeholder="$t('Select')"
              :options="statusOptions"
              item="label"
            />
          </b-col>
          <!-- Start Date -->
          <b-col md="6">
            <DatePickerField
              id="start-date"
              v-model="filter.startDate"
              :min-date="new Date()"
              :label="$t('Start Date')"
              :config="datePickerConfig"
              :placeholder="$t('Start Date')"
            />
          </b-col>
          <!-- Start Date -->
          <!-- End Date -->
          <b-col md="6">
            <DatePickerField
              id="end-date"
              v-model="filter.endDate"
              :min-date="new Date()"
              :label="$t('End Date')"
              :config="datePickerConfig"
              :placeholder="$t('End Date')"
            />
          </b-col>
          <!-- End Date -->
        </b-row>
      </b-form>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem, BBadge,
} from 'bootstrap-vue'
import { required } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import {
  toBalance, toFormatAmount, formatAmount,
} from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import toNumber from '../../../../helpers/toNumber'
import {
  toCustomDateTime, toCustomDate, toDateWithYearFirst, toDateWithCustomTime,
} from '../../../../helpers/formatDate'
import getStatusColor from '../../../../helpers/getStatusColor'
import { operationPermissions } from '@/helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    DatePickerField,
  },
  directives: {
    Ripple,
  },
  mixins: [sweetAlert, modalMixins, tableMixins, permissionMixin],
  data() {
    return {
      toBalance,
      toDateWithCustomTime,
      getStatusColor,
      toFormatAmount,
      formatAmount,
      formatToDateWithYears: toDateWithYearFirst,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isDisabled: false,
      isBusy: false,
      required,
      modalTitle: this.$t('View SMS Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'chargeAccount', sortable: true, label: 'Charge Account' },
        { key: 'institutionName', sortable: true, label: 'Sender Name' },
        { key: 'gateway', sortable: true, label: 'SMS Gateway' },
        { key: 'smsPageCount', sortable: true, label: 'SMS Page Count' },
        { key: 'message', sortable: true, label: 'Message' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'deliveryStatus', sortable: true, label: 'Delivery Status' },
        { key: 'status', label: 'Status' },
      ],
      filter: {
        phoneNumber: '',
        accountNumber: '',
        referenceNumber: '',
        startDate: null,
        endDate: null,
        status: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: '',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      selected: '',
      statusOptions: ['Pending', 'Successful', 'Failed'],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchSMSReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchSMSReport()
    },
  },
  mounted() {
    this.fetchSMSReport()
  },
  methods: {
    ...mapActions({
      getSMSReports: 'operations/getSMSReportLists',
      downloadSMSReports: 'reports/downloadSMSReports',
    }),
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchSMSReport()
    },
    async fetchSMSReport() {
      try {
        let myStatus
        if (this.filter.status === 'Pending') {
          myStatus = 1
        } else if (this.filter.status === 'Successful') {
          myStatus = 2
        } else if (this.filter.status === 'Failed') {
          myStatus = 3
        } else {
          myStatus = null
        }
        const filter = {
          ...this.filter,
          status: myStatus,
        }
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getSMSReports({
          search: filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: operationPermissions.VIEW_SMS_REPORT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data.map(item => ({
            ...item,
            chargeAccount: item.chargeAccount === true ? 'Yes' : 'No',
            // eslint-disable-next-line no-nested-ternary
            status: (item.status === 1) ? 'Pending' : (item.status === 2) ? 'Successful' : 'Failed',
          }))
          this.totalCount = data.totalItems
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        let myStatus
        if (this.filter.status === 'Pending') {
          myStatus = 1
        } else if (this.filter.status === 'Successful') {
          myStatus = 2
        } else if (this.filter.status === 'Failed') {
          myStatus = 3
        } else {
          myStatus = null
        }
        const filter = {
          ...this.filter,
          status: myStatus,
        }
        const response = await this.downloadSMSReports({
          search: filter,
          fileExportFormat: 1,
          permission: operationPermissions.VIEW_SMS_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your ',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        let myStatus
        if (this.filter.status === 'Pending') {
          myStatus = 1
        } else if (this.filter.status === 'Successful') {
          myStatus = 2
        } else if (this.filter.status === 'Failed') {
          myStatus = 3
        } else {
          myStatus = null
        }

        const filter = {
          ...this.filter,
          status: myStatus,
        }
        const response = await this.downloadSMSReports({
          search: filter,
          fileExportFormat: 0,
          permission: operationPermissions.VIEW_SMS_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatNumber: toNumber,
    formatDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
